// frontend/src/components/loading/PageBodySuspense.js
//
// A Suspense boundary for a lazy page that sits INSIDE AuthLayout, so the real
// navbar and footer render at once and only the page body shows a skeleton
// while the page's chunk downloads (/settings, /admin, /admin/users/:userId):
//
//   <AuthLayout>
//     <PageBodySuspense fallback={<LazySettingsPageSkeleton />}>
//       <Settings />
//     </PageBodySuspense>
//   </AuthLayout>
//
// WHY NOT A PLAIN <Suspense>. AuthLayout hands the page its language by
// CLONING its one child with `{ isHebrew, toggleLanguage }`. A bare <Suspense>
// there would receive those props itself and drop them, and the page would
// render with `isHebrew` undefined - English, in a Hebrew app. This boundary
// takes whatever its parent injects and passes it on to the page, so the page
// receives exactly what it received as AuthLayout's direct child.
//
// The FALLBACK receives the same injected props (when it is an element), so
// the skeleton speaks the page's language too - its hidden loading label
// and its per-language breakpoints follow AuthLayout's toggle exactly as the
// page will, instead of a language read once from storage.
//
// Consequence, by design: on these routes AuthLayout commits BEFORE the page
// chunk is in, so its mount effect (releaseAuthBoot) takes the boot splash down
// onto navbar + skeleton - a real page frame - rather than holding it until the
// page itself paints. A chunk that fails to load throws out of this boundary to
// the same error path as before; this component adds no handling of its own.

import React, { Suspense } from "react";

/**
 * @param {object} props
 * @param {React.ReactNode} props.fallback what the page body shows while the
 *        page's lazy chunk loads; an element receives the injected props too
 * @param {React.ReactElement} props.children the ONE lazy page element -
 *        anything else throws React.Children.only's error at render, a
 *        wiring mistake caught on the first load of the route
 * @param {...*} injected the props the parent layout injects (AuthLayout's
 *        `isHebrew`, `toggleLanguage`), forwarded to the page and the fallback
 *        unchanged
 */
function PageBodySuspense({ fallback, children, ...injected }) {
  const page = React.Children.only(children);
  const body = React.isValidElement(fallback) ? React.cloneElement(fallback, injected) : fallback;
  return <Suspense fallback={body}>{React.cloneElement(page, injected)}</Suspense>;
}

export default PageBodySuspense;
